"use client";
import { useState, useEffect } from "react";
import { brandList } from "@/lib/brandData";
import DynamicImage from "@/components/DynamicImages/DynamicImage";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Link from "next/link";
import { useQueryParams } from "@/utils/useQueryParams";
import { AddNewBreadcrum } from "@/redux/slices/breadcrumSlice";
import { useDispatch } from "react-redux";
// Alphabet list from A to Z
const alphabet = [
  "All",
  ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
];

const BrandImageTag = ({ src, link, title, updateBreadcrumb }) => (
  <li className="px-5 py-4 bg-white rounded-[12px] border-[1px] border-[#F0F0F0] shadow-lg p-2 h-[129px] flex items-center justify-center hover:underline cursor-pointer transition-transform duration-300 transform hover:scale-105">
    <Link
      href={link !== "#" ? `/${link}` : "#"}
      passHref
      onClick={() => updateBreadcrumb([{name: "All Brands", url: "/brands"} ,{name: title, url: `/${link}`}])}
      {...(link !== "#" && { rel: "noopener noreferrer" })}
      rel="noopener noreferrer"
      className="grid grid-cols-1 items-center text-center"
    >
      <div className="w-full h-[50px] flex items-center justify-center overflow-hidden ">
        <DynamicImage
          src={src}
          className="h-full object-contain max-w-[135px] mob:max-w-full"
          alt="Brand Logo"
        />
      </div>
      <b className="text-[12px] font-medium pt-4">{title}</b>
    </Link>
  </li>
);

const BrandContainer = () => {
  const { type } = useQueryParams();

  const [selectedOption, setSelectedOption] = useState("All");
  const [selectedLetter, setSelectedLetter] = useState("All");
  const dispatch = useDispatch();

  useEffect(() => {
    if (type) {
      setSelectedOption("Luxury");
    }
  }, [type]);

  const handleMajorChange = (value) => {
    setSelectedOption(value);
  };

  const handleLetterClick = (letter) => {
    setSelectedLetter(letter);
  };

  // Filter brands based on selected letter and category
  const getFilteredBrands = (type) => {
    const filtered =
      selectedLetter === "All"
        ? brandList.filter((brand) => brand[type])
        : brandList.filter(
            (brand) => brand[type] && brand.name.startsWith(selectedLetter)
          );

    return filtered.sort((a, b) => a?.name?.localeCompare(b?.name));
  };

  const filteredBrands = {
    Luxury: getFilteredBrands("isLuxury"),
    Major: getFilteredBrands("isMajor"),
    All: getFilteredBrands("isAll"),
  };

  const updateBreadcrumb = (breadcrumb) => {
    dispatch(AddNewBreadcrum(breadcrumb));
  };
  
  return (
    <div className="container-fluid max-w-full">
      <div className="container mx-auto p-0 bg-white text-black py-[64px] lg:py-[28px]">
        <section className="bg-white text-black container max-w-1440 px-7 mx-auto h-auto relative lg:px-4">
          <div className="lg:mb-[32px]">
            <h1 className="text-4xl leading-[54px] font-bold text-center lg:text-[22px] lg:leading-[24px]">
              Our Brands
            </h1>
            <h4 className="text-md font-normal text-center p-2 lg:text-[16px] lg:leading-[24px]">
              Discover from various brands
            </h4>
          </div>
          <div className="inline-flex items-center gap-5 absolute right-4 top-[25%] lg:relative lg:top-0 lg:right-0 lg:justify-end lg:gap-[10px]">
            <span className="text-gray3 lg:text-[13px] lg:leading-[24px]">
              Filter By
            </span>
            <Select onValueChange={handleMajorChange} value={selectedOption}>
              <SelectTrigger className="w-[205px] h-[44px] rounded-[10px] border-gray7 font-medium lg:w-[175px]">
                <SelectValue placeholder="Luxury" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="All">All</SelectItem>
                  <SelectItem value="Luxury">Luxury</SelectItem>
                  <SelectItem value="Major">Major</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </section>
      </div>

      <div className="container-fluid max-w-full bg-[#ffffff] mx-auto px-4 pb-[20px]">
        <section className="container max-w-1440 px-7 lg:px-0">
          {/* A-Z Navigation with "All" option */}
          <div className="flex justify-center mb-4 flex-wrap">
            {alphabet.map((letter) => (
              <button
                key={letter}
                className={`px-3 py-1 m-1 font-normal ${
                  selectedLetter === letter
                    ? "bg-gray-600 text-white"
                    : "bg-gray-100 text-black"
                } rounded`}
                onClick={() => handleLetterClick(letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="container-fluid max-w-full bg-[#ffffff] mx-auto px-4 pb-[58px]">
        <section className="container max-w-1440 px-7 lg:px-0">
          <ul className="grid grid-cols-215 gap-[15px] mt-6  md:gap-[20px] justify-center items-center lg:gap-x-3 lg:gap-y-[22px] mob:grid-cols-2">
            {filteredBrands[selectedOption].map((brand, index) => (
              <BrandImageTag
                key={index}
                src={brand.src}
                link={brand.link}
                title={brand.name}
                updateBreadcrumb={updateBreadcrumb}
              />
            ))}
          </ul>
          {/* Show a message if no brands are found */}
          {filteredBrands[selectedOption].length === 0 && (
            <div className="max-w-[1480px] mx-auto my-0 py-14 px-5 lg:py-11 pb-[40px]">
              <h2 className="text-[21px] leading-[31px] font-bold text-blue2 text-center lg:text-[16px] lg:leading-[22px] lg:mb-[27px]">
                No brands found.
              </h2>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
export default BrandContainer;